/**
 * Centralized i18n keys used across the app.
 *
 * Goals:
 * - Single source of truth → renaming a key only happens here.
 * - Type-safe usage with the `t()` helper (no typos in templates).
 * - Each entry pairs a translation key with a French fallback used when the
 *   key is missing in the active locale (matches the project's i18n strategy
 *   of always providing a sensible default).
 */

export type I18nEntry = {
  /** Dot-separated translation key passed to `t(...)`. */
  key: string;
  /** Default French label used as the second arg of `t(key, fallback)`. */
  fallback: string;
};

/**
 * Titles displayed above the mobile collapsed `TabsList` dropdown.
 * Keep keys flat & descriptive — one entry per page that uses tabs.
 */
export const MOBILE_TAB_TITLES = {
  prospects: { key: "prospects.title", fallback: "Prospects" },
  content: { key: "content.title", fallback: "Contenu" },
  events: { key: "events.title", fallback: "Événements" },
  prospectDetail: { key: "prospect.detailTitle", fallback: "Fiche prospect" },
} as const satisfies Record<string, I18nEntry>;

export type MobileTabTitleKey = keyof typeof MOBILE_TAB_TITLES;

/**
 * Tab labels per page. Grouped per page so each page can iterate to render
 * its `<TabsTrigger>` list — guarantees no empty value, no duplicated key,
 * and a single place to rename a tab.
 *
 * The `value` here is also the Radix `<TabsTrigger value="...">` value.
 */
export type TabLabel = I18nEntry & { value: string };

export const CONTENT_TABS = {
  list: { value: "list", key: "content.tabs.list", fallback: "Liste" },
  calendar: { value: "calendar", key: "content.tabs.calendar", fallback: "Calendrier" },
} as const satisfies Record<string, TabLabel>;

export type ContentTabKey = keyof typeof CONTENT_TABS;

